import { ref } from 'vue'
import { defineStore } from 'pinia'

/**
 * File de messages éphémères (toasts). Un store et non un `provide/inject` :
 * n'importe quel module — y compris `api/client`, qui n'est pas un composant —
 * doit pouvoir signaler une erreur sans avoir accès à l'arbre de composants.
 */

export type ToastKind = 'info' | 'success' | 'warning' | 'error'

export interface Toast {
  id: number
  kind: ToastKind
  message: string
}

/** Durées par nature du message, en millisecondes. */
const TTL: Record<ToastKind, number> = {
  info: 4000,
  success: 3000,
  warning: 6000,
  // Une erreur reste plus longtemps : c'est le seul message que l'utilisateur
  // doit avoir le temps de lire en entier, et éventuellement de recopier.
  error: 9000,
}

let nextId = 1

export const useNotificationsStore = defineStore('notifications', () => {
  const toasts = ref<Toast[]>([])

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((t) => t.id !== id)
  }

  function push(kind: ToastKind, message: string): number {
    const id = nextId++
    toasts.value.push({ id, kind, message })

    // Plafond dur : une boucle qui échoue (un intervalle qui appelle une API
    // morte) remplirait l'écran en quelques secondes. On garde les plus récents.
    if (toasts.value.length > 5) toasts.value.shift()

    window.setTimeout(() => dismiss(id), TTL[kind])
    return id
  }

  const info = (m: string) => push('info', m)
  const success = (m: string) => push('success', m)
  const warning = (m: string) => push('warning', m)
  const error = (m: string) => push('error', m)

  return { toasts, push, dismiss, info, success, warning, error }
})
